<template>
  <v-row v-if="shownItems && !shownItems.length">
    <v-col>
      <v-alert color="info" type="info">
        We’re sorry, there are no matching trainers.
      </v-alert>
    </v-col>
  </v-row>

  <v-row v-if="shownItems" justify="space-evenly">
    <template v-if="shownItems.length === 1">
      <template v-for="item in shownItems" :key="item.id">
        <v-col cols="12">
          <PersonCardWithDescription
            :href="`/trainers/${item.page_slug}`"
            :value="item"
            image-key="list_image"
          >
            <template #image-content>
              <v-btn
                :href="`/trainers/${item.page_slug}`"
                :icon="mdiArrowRight"
                class="ma-2"
                color="primary"
                position="absolute"
                style="top: 0; right: 0"
              />
            </template>
          </PersonCardWithDescription>
        </v-col>
      </template>
    </template>

    <template v-else-if="shownItems.length">
      <template v-for="item in shownItems" :key="item.id">
        <v-col cols="12" sm="4" md="3" xl="2">
          <PersonCard
            :href="`/trainers/${item.page_slug}`"
            :value="item"
            image-key="list_image"
          />
        </v-col>
      </template>
    </template>
  </v-row>

  <v-row v-if="showAllButton && hasMore">
    <v-col>
      <v-btn color="red-darken-2" block size="large" @click="isAllShown = true"
        >Show All</v-btn
      ></v-col
    >
  </v-row>

  <slot name="footer" :shown="shownItems && shownItems.length" />
</template>

<script setup>
import { computed, ref } from 'vue'
import { FILTER_WHAT_ALL } from '#root/lib/consts'
import { useOccasionsStoreOrThrow } from '#root/lib/useOccasionsStore'
import { useTrainerPersonsStoreOrThrow } from '#root/lib/useTrainerPersonsStore'
import { mdiArrowRight } from '@mdi/js'
const props = defineProps({
  all: {
    default: false,
    type: Boolean
  },
  filtersValue: {
    default: () => {},
    type: Object
  },
  limit: {
    default: 10000,
    type: Number
  },
  showAllButton: {
    default: false,
    type: Boolean
  },
  upcoming: {
    default: false,
    type: Boolean
  },
  upcomingLimit: {
    default: 12,
    type: Number
  }
})

const isAllShown = ref(false)
const {
  indexes,
  items: occasionItems,
  upcomingIndicies
} = useOccasionsStoreOrThrow()
const { items: personItems } = useTrainerPersonsStoreOrThrow()
const filteredItems = computed(() => {
  if (!personItems.value) return []

  const filters = props.filtersValue || {}
  const search = filters.search && filters.search.toLowerCase().trim()

  return personItems.value.filter(person => {
    const productCodes = person.products
      ? person.products.map(product => product.products_id.code)
      : []

    return (
      (!search || person.name.toLowerCase().includes(search)) &&
      (filters.what === FILTER_WHAT_ALL ||
        (typeof filters.what === 'string' &&
          productCodes.includes(filters.what.toUpperCase())))
    )
  })
})
const upcomingItems = computed(() => {
  const filters = props.filtersValue || {}

  let indicies = upcomingIndicies.value
  let items = null

  if (personItems.value && occasionItems.value && indicies) {
    items = new Map()

    //
    // Apply the occasion filters first
    //

    if (Array.isArray(filters.productCodes) && filters.productCodes.length) {
      let ind = []
      filters.productCodes.forEach(
        value =>
          (ind = ind.concat(
            (indexes.value.product_code &&
              indexes.value.product_code[value.toUpperCase()]) ||
              []
          ))
      )
      indicies = indicies.filter(i => ind.includes(i))
    }

    if (typeof filters.what === 'string' && filters.what !== FILTER_WHAT_ALL) {
      const ind =
        (indexes.value.product_code &&
          indexes.value.product_code[filters.what.toUpperCase()]) ||
        []
      indicies = indicies.filter(i => ind.includes(i))
    }

    for (const ind of indicies) {
      if (items.size >= props.upcomingLimit) break

      const occasion = occasionItems.value[ind]
      const personCode = occasion?.product_variant?.person.code

      if (personCode && !items.has(personCode)) {
        const person = personItems.value.find(({ code }) => code === personCode)
        if (person) items.set(personCode, person)
      }
    }

    items = [...items.values()]
  }

  return items
})
const shownItems = computed(() => {
  return props.all || isAllShown.value
    ? filteredItems.value
    : props.upcoming && upcomingItems.value && upcomingItems.value.length
      ? upcomingItems.value
      : filteredItems.value.slice(0, props.limit)
})
const hasMore = computed(() => {
  return (
    filteredItems.value &&
    shownItems.value &&
    filteredItems.value.length > shownItems.value.length
  )
})
</script>
